import { z } from 'zod';
import { themeSchema } from '@loop/shared';
/**
 * Public Streamer REST DTO — Phase 7 / Plan 07-03 (HUB-02).
 *
 * 엔드포인트 (공개 — SessionMiddleware `.exclude('api/v1/public/*splat')`):
 *   - `GET /api/v1/public/streamers/:slug` — `/s/{slug}` 스트리머 공개 홈 shell.
 *
 * 핵심 불변 (ENG-REVIEW A3 / STACK.md Pitfall 2):
 *   - **LIVE 상태 필드는 절대 포함되지 않는다** — `isLive` · `viewerCount` 키는 스키마에
 *     정의조차 하지 않는다. ISR 60s 캐시가 LIVE 상태를 보관하면 방송 시작/종료 전환
 *     지연이 최대 60 초까지 누적되므로, LIVE 는 항상 클라이언트 `LiveBadge` 컴포넌트가
 *     `/sse/presence/:streamerId` SSE 로 수신해 실시간 반영한다.
 *   - 서버가 혹시 실수로 `isLive` 를 직렬화해도 Zod strict parse 로 drop 되거나 reject.
 *
 * 위협 모델 (07-03-PLAN §threat_model):
 *   - T-07-03-01 (I): email / phone / kyc_* 등 PII 컬럼 미선언 — controller select 도 한정.
 *   - T-07-03-07 (I): ISR 캐시 LIVE 상태 포함 금지 — schema 에서 필드 자체 부재.
 *
 * Wave 0 / Wave 1 범위:
 *   - avatarUrl / bannerUrl: Wave 0 은 `users.image` → avatarUrl 만 매핑, bannerUrl 은 항상
 *     null (streamers 테이블에 banner 컬럼 없음). Wave 1 의 `streamer_branding` 테이블 도입
 *     시 확장. 07-03-SUMMARY.md 가 Deferred 에 기록.
 *   - scheduleStub / shopStub: 항상 `[]` — Wave 1 community/schedule/shop 탭 확장 hook.
 */
/**
 * 공개 스트리머 프로필 응답.
 *
 * **LIVE 상태는 포함하지 않음** — SSE `/sse/presence/:streamerId` 를 통해 클라이언트가 별도 수신.
 */
export const publicStreamerResponseSchema = z
    .object({
    streamerId: z.string().uuid(),
    channelSlug: z.string().min(1).max(64),
    displayName: z.string(),
    bio: z.string().nullable(),
    avatarUrl: z.string().url().nullable(),
    bannerUrl: z.string().url().nullable(),
    /**
     * 스트리머 theme.json 파싱 결과. parse 실패 시 apps/api 의
     * `WidgetThemeService.getTheme(streamerId)` 가 이미 `defaultTheme` 폴백을 반환하므로
     * 본 필드는 항상 valid Theme.
     */
    theme: themeSchema,
    /**
     * 후원 가능 여부. `streamers.toss_seller_status in ('PARTIALLY_APPROVED',
     * 'FULLY_APPROVED')` 일 때만 true. Plan 07-04 `/@{channelSlug}` 결제 페이지로의
     * CTA 활성화 판정에 사용.
     */
    donateEnabled: z.boolean(),
    /**
     * Plan 07-05 확장 (CHAT-04) — LOOP 네이티브 채팅 입력 활성 플래그.
     *   - `streamers.chat_native_enabled` 컬럼 그대로 투영.
     *   - Plan 07-04 LiveTab 이 이 값으로 ChatComposer 렌더 여부 결정.
     *   - false 면 읽기 전용 overlay. true 면 composer 노출 (+ tierName gate).
     */
    chatNativeEnabled: z.boolean(),
    /**
     * Plan 07-05 확장 (CHAT-04) — 네이티브 채팅 최소 구독 티어 이름.
     *   - `streamers.chat_native_required_tier_id` 가 NULL 이면 null (모든 로그인 허용).
     *   - 설정 시 `subscription_tiers.name` JOIN 결과 (UX 목적 — 티어 id/priceKrw 노출 금지).
     *   - ChatComposer 가 `canSend=false` 일 때 "{tierName} 이상 구독자만 입력 가능" 배너 렌더.
     */
    chatNativeRequiredTierName: z.string().nullable(),
    /** Wave 0 은 항상 `[]`. Wave 1 schedule 탭 확장 시 내용 도입. */
    scheduleStub: z.array(z.unknown()).max(0),
    /** Wave 0 은 항상 `[]`. Wave 1 shop 탭 확장 시 내용 도입. */
    shopStub: z.array(z.unknown()).max(0),
    /** 서버 snapshot 시각 — ISR hit 여부 관측 참고. */
    fetchedAt: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * Internal revalidate 요청 — apps/api → apps/web.
 *
 * 사용처:
 *   - apps/api WidgetThemeService.saveTheme 성공 후 (Wave 1 에서 실제 배선).
 *   - apps/api StreamerProfileController profile update 성공 후 (Wave 1).
 *   - Wave 0 범위: controller + Zod + 수동 호출 가능 상태까지.
 *
 * 경계:
 *   - X-Internal-Secret 헤더로 서명 검증 (AUTH_INTERNAL_SECRET env — 컨테이너 내부 네트워크
 *     제한과 함께 이중 방어).
 *   - Traefik 외부 노출 금지 — Coolify 설정에서 route `/api/internal/*` 를 internal
 *     network 에서만 도달 가능하도록.
 */
export const revalidateStreamerRequestSchema = z.object({
    /** 다음 중 하나는 반드시 있어야 함 — slug 기준 태그 식별. */
    channelSlug: z.string().min(1).max(64),
    /** 옵션 — 감사/로그용. Wave 1 `streamer_actions` 테이블에 기록 시 활용. */
    streamerId: z.string().uuid().optional(),
    /** 무효화 트리거 — 감사 로그 구분용. */
    reason: z.enum(['profile', 'theme', 'shop', 'schedule']),
});
/**
 * Internal revalidate 응답 (apps/web → caller).
 */
export const revalidateStreamerResponseSchema = z.object({
    revalidated: z.boolean(),
    tag: z.string(),
});
//# sourceMappingURL=public-streamer.rest.js.map